import { Instance, Instances } from "@react-three/drei";
import type { Euler } from "@react-three/fiber";
import { Fragment, useMemo } from "react";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry";
import { useCabinetStore } from "../../../context/StoreContext";
import { getDimensions } from "../../../hooks/getDimensions";
import { useMaterialContext } from "../context/MaterialContext";
import { CORNER_SMOOTHING, getCornerRounding, getShouldHaveBlindVertical } from "../data";
import type { CompartmentType } from "../data/Compartment";
import { type Shelf as ShelfInstance, getShelfHoleYPositions } from "../data/Shelf";

interface Props {
	shelf: ShelfInstance;
}

export function Shelf({ shelf }: Props) {
	const { state, api } = useCabinetStore();
	const { getMaterial } = useMaterialContext();
	const cornerRounding: number = getCornerRounding(state, api);

	const { geometry, dimensions } = useMemo(() => {
		const dimensions = getDimensions(shelf);

		return {
			dimensions,
			geometry: new RoundedBoxGeometry(
				dimensions.width,
				dimensions.height,
				dimensions.depth,
				CORNER_SMOOTHING,
				cornerRounding,
			),
		};
	}, [shelf, cornerRounding]);

	const material = getMaterial({
		variant: "shelf",
		width: shelf.width,
		height: shelf.height,
		depth: shelf.depth,
	});

	return (
		<mesh
			name={shelf.id}
			receiveShadow
			castShadow
			position={[dimensions.x, dimensions.y, dimensions.z]}
			rotation={[0, 0, 0]}
			material={material}
			geometry={geometry}
		/>
	);
}

export const DRILLING_HOLE_DIAMETER = 0.005;

export function DrillingHoles({ compartment }: { compartment: CompartmentType }) {
	const { state } = useCabinetStore();

	const dimensions = getDimensions(compartment);
	const hasBlindVertical = getShouldHaveBlindVertical(compartment);
	const yDrillPositions = useMemo(() => getShelfHoleYPositions(compartment), [compartment]);

	const rotation: Euler = [0, 0, Math.PI / 2];
	const INSERT_HOLE_Z_OFFSET = 0.055;

	const zPositions = [
		dimensions.z - INSERT_HOLE_Z_OFFSET + dimensions.depth / 2,
		dimensions.z - +dimensions.depth / 2 + 0.01 + INSERT_HOLE_Z_OFFSET,
	];

	const multiplier = hasBlindVertical ? 8 : 4;
	const limit = yDrillPositions.length * multiplier;

	return (
		<Instances key={limit} limit={limit}>
			<cylinderGeometry args={[DRILLING_HOLE_DIAMETER, DRILLING_HOLE_DIAMETER, 0.005, 32, 1]} />
			<meshStandardMaterial color={0x6b6b6b} />

			{zPositions.map((z) => (
				<Fragment key={z}>
					{yDrillPositions.map((y, i) => (
						<Instance
							key={i}
							position={[dimensions.x - dimensions.width / 2, y, z]}
							rotation={rotation}
						/>
					))}

					{yDrillPositions.map((y, i) => (
						<Instance
							key={i}
							position={[dimensions.x + dimensions.width / 2, y, z]}
							rotation={rotation}
						/>
					))}

					{hasBlindVertical && (
						<Fragment>
							{yDrillPositions.map((y, i) => (
								<Instance
									key={i}
									position={[dimensions.x - state.thickness / 1000 / 2, y, z]}
									rotation={rotation}
								/>
							))}

							{yDrillPositions.map((y, i) => (
								<Instance
									key={i}
									position={[dimensions.x + state.thickness / 1000 / 2, y, z]}
									rotation={rotation}
								/>
							))}
						</Fragment>
					)}
				</Fragment>
			))}
		</Instances>
	);
}
